import { GAME_CONFIG } from '../config';
import { GameState as IGameState, StateChanges, DialogueHistoryEntry, GameSave } from '../types/index';

export class GameState {
    private state: IGameState;
    private currentSceneDescription: string;

    constructor() {
        this.state = this.getInitialState();
        this.currentSceneDescription = '';
    }

    private getInitialState(): IGameState {
        return {
            currentScene: GAME_CONFIG.defaultScene,
            inventory: [],
            flags: {},
            relationships: {},
            dialogueHistory: []
        };
    }

    getState(): IGameState {
        return this.state;
    }

    setState(newState: Partial<IGameState>): void {
        this.state = { ...this.state, ...newState };
    }

    setCurrentSceneDescription(description: string): void {
        this.currentSceneDescription = description;
    }

    getCurrentSceneDescription(): string {
        return this.currentSceneDescription;
    }

    update(changes: StateChanges): void {
        if (changes.inventory) {
            this.state.inventory = changes.inventory;
        }
        if (changes.flags) {
            this.state.flags = { ...this.state.flags, ...changes.flags };
        }
        if (changes.relationships) {
            this.state.relationships = { ...this.state.relationships, ...changes.relationships };
        }
    }

    /**
     * 添加对话记录
     * @param scene 场景名称
     * @param playerChoice 玩家选择
     * @param npcResponse NPC回应
     */
    addDialogueEntry(scene: string, playerChoice: string, npcResponse: string | undefined): void {
        try {
            console.log('添加对话记录:', {
                scene,
                playerChoice,
                npcResponse,
                currentDescription: this.currentSceneDescription
            });

            if (!scene || !playerChoice) {
                console.warn('添加对话记录失败: 缺少必要参数');
                return;
            }

            const entry: DialogueHistoryEntry = {
                scene,
                scene_description: this.currentSceneDescription || '',
                player_choice: playerChoice,
                npc_response: npcResponse || '',
                timestamp: new Date().toISOString()
            };

            this.state.dialogueHistory.push(entry);
            console.log('对话记录已添加:', entry);
        } catch (error) {
            console.error('添加对话记录时出错:', error);
        }
    }

    getDialogueHistory(): DialogueHistoryEntry[] {
        return this.state.dialogueHistory;
    }

    exportHistory(): string {
        return JSON.stringify({
            version: GAME_CONFIG.version,
            timestamp: new Date().toISOString(),
            state: {
                currentScene: this.state.currentScene,
                currentSceneDescription: this.currentSceneDescription,
                inventory: this.state.inventory,
                flags: this.state.flags,
                relationships: this.state.relationships,
                dialogueHistory: this.state.dialogueHistory
            }
        }, null, 2);
    }

    importHistory(data: any): boolean {
        try {
            if (!this.validateSaveData(data)) {
                throw new Error('无效的存档格式');
            }

            // 导入状态
            this.state.currentScene = data.state.currentScene;
            this.currentSceneDescription = data.state.currentSceneDescription;
            this.state.inventory = data.state.inventory;
            this.state.flags = data.state.flags;
            this.state.relationships = data.state.relationships;
            this.state.dialogueHistory = data.state.dialogueHistory;

            return true;
        } catch (error) {
            console.error('导入存档失败:', error);
            return false;
        }
    }

    private validateSaveData(data: any): data is GameSave {
        try {
            // 1. 基本结构验证
            if (!data || typeof data !== 'object') {
                console.error('存档数据不是有效的对象');
                return false;
            }

            // 2. 版本和时间戳验证
            if (!data.version || !data.timestamp) {
                console.error('缺少版本号或时间戳');
                return false;
            }

            // 3. 状态对象验证
            if (!data.state || typeof data.state !== 'object') {
                console.error('缺少状态数据或状态数据不是对象');
                return false;
            }

            const state = data.state;

            // 4. 必要字段类型验证
            const typeChecks = {
                currentScene: 'string',
                currentSceneDescription: 'string',
                inventory: Array.isArray(state.inventory),
                flags: 'object',
                relationships: 'object',
                dialogueHistory: Array.isArray(state.dialogueHistory)
            };

            for (const [field, expectedType] of Object.entries(typeChecks)) {
                if (typeof expectedType === 'string') {
                    if (typeof state[field] !== expectedType) {
                        console.error(`字段 ${field} 类型错误，期望 ${expectedType}，实际是 ${typeof state[field]}`);
                        return false;
                    }
                } else if (!expectedType) {
                    console.error(`字段 ${field} 不是数组`);
                    return false;
                }
            }

            // 6. 对话历史记录验证
            if (!state.dialogueHistory.every((entry: DialogueHistoryEntry) => {
                const valid = typeof entry.scene === 'string' &&
                    typeof entry.scene_description === 'string' &&
                    typeof entry.player_choice === 'string' &&
                    typeof entry.npc_response === 'string' &&
                    typeof entry.timestamp === 'string';

                if (!valid) {
                    console.error('对话历史记录条目格式错误:', entry);
                }

                return valid;
            })) {
                console.error('对话历史记录验证失败');
                return false;
            }

            return true;
        } catch (error) {
            console.error('验证存档数据时出错:', error);
            return false;
        }
    }

    save(): boolean {
        try {
            const saveData: GameSave = {
                version: GAME_CONFIG.version,
                timestamp: new Date().toISOString(),
                state: {
                    ...this.state,
                    currentSceneDescription: this.currentSceneDescription
                }
            };
            localStorage.setItem(GAME_CONFIG.saveKey, JSON.stringify(saveData));
            return true;
        } catch (error) {
            console.error('保存游戏状态失败:', error);
            return false;
        }
    }

    load(): boolean {
        try {
            const savedState = localStorage.getItem(GAME_CONFIG.saveKey);
            if (savedState) {
                const data = JSON.parse(savedState);
                if (this.validateSaveData(data)) {
                    this.state = data.state;
                    this.currentSceneDescription = data.state.currentSceneDescription;
                    return true;
                }
            }
            return false;
        } catch (error) {
            console.error('加载游戏状态失败:', error);
            return false;
        }
    }

    reset(): void {
        this.state = this.getInitialState();
        this.currentSceneDescription = '';
    }

    // 便捷方法
    addToInventory(item: string): void {
        if (!this.state.inventory.includes(item)) {
            this.state.inventory.push(item);
        }
    }

    removeFromInventory(item: string): void {
        this.state.inventory = this.state.inventory.filter(i => i !== item);
    }

    setFlag(flag: string, value: boolean = true): void {
        this.state.flags[flag] = value;
    }

    getFlag(flag: string): boolean {
        return !!this.state.flags[flag];
    }

    setRelationship(npc: string, value: number): void {
        this.state.relationships[npc] = value;
    }

    getRelationship(npc: string): number {
        return this.state.relationships[npc] || 0;
    }
} 